import { Fragment, useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  X,
  Receipt,
  History,
  ArrowRight,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { confirmationsService } from "../services/confirmations.service";
import { UserAvatar } from "@/shared/components/UserAvatar";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { formatDurationMinutes } from "@/shared/utils/durationHelpers";
import type {
  ConfirmationHistoryItem,
  ConfirmationHistoryType,
  OperationStatus,
} from "../types/confirmations-api.types";
import useTranslations from "@/shared/lang/useTranslations";

type ConfirmationHistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  confirmationId: string;
  confirmationReference?: string;
};

function getStatusBadgeClass(status: OperationStatus): string {
  switch (status) {
    case "CONFIRMED":
      return "bg-green-500/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-500/20";
    case "PENDING":
      return "bg-amber-100 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-500/20";
    case "CANCELLED":
      return "bg-rose-100 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-200 dark:border-rose-500/20";
    case "ARCHIVED":
      return "bg-slate-200 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600";
    case "NOTIFIED":
      return "bg-blue-100 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-500/20";
    default:
      return "bg-slate-200 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600";
  }
}

export const ConfirmationHistoryModal = ({
  isOpen,
  onClose,
  confirmationId,
}: ConfirmationHistoryModalProps) => {
  const { t } = useTranslations("confirmations");
  const [history, setHistory] = useState<ConfirmationHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const operationStatusLabels: Record<OperationStatus, string> = {
    PENDING: t("status_pending"),
    CONFIRMED: t("status_confirmed"),
    CANCELLED: t("status_cancelled"),
    ARCHIVED: t("status_archived"),
    NOTIFIED: t("status_notified"),
  };

  const changeTypeLabels: Record<ConfirmationHistoryType, string> = {
    CREATED: t("change_type_created"),
    STATUS_CHANGE: t("change_type_status"),
    DATA_UPDATE: t("change_type_data"),
    ATTACHMENT_ADDED: t("change_type_attachment"),
    NOTE_ADDED: t("change_type_note"),
  };

  const fetchHistory = useCallback(async () => {
    if (!confirmationId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await confirmationsService.fetchHistory(confirmationId, {
        skip: 0,
        limit: 100,
      });
      const sorted = [...response.data].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
      const seen = new Set<string>();
      const deduplicated = sorted.filter((item) => {
        const ts = new Date(item.created_at).getTime();
        const minuteKey = Math.floor(ts / 60000) * 60000;
        const key = `${item.change_type}|${minuteKey}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setHistory(deduplicated);
    } catch {
      setError(t("modal_history_error"));
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [confirmationId, t]);

  useEffect(() => {
    if (isOpen && confirmationId) {
      fetchHistory();
    }
  }, [isOpen, confirmationId, fetchHistory]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            aria-hidden
            onClick={onClose}
          />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto p-4">
          <div className="flex min-h-full items-center justify-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel
                className="w-full max-w-2xl transform overflow-hidden rounded-xl border border-slate-200 dark:border-white/5 bg-white dark:bg-[#1c2a17] shadow-2xl transition-all"
                onClick={(e) => e.stopPropagation()}
              >
                <header className="flex items-center justify-between border-b border-slate-100 dark:border-white/10 px-8 py-6">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-green-500/20 p-2">
                      <Receipt className="h-6 w-6 text-green-500 dark:text-green-400" />
                    </div>
                    <h2 className="text-xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white">
                      {t("modal_history_title")}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 dark:bg-[#2d3829] text-slate-600 dark:text-white transition-colors hover:bg-slate-200 dark:hover:bg-green-500/20"
                    aria-label={t("modal_history_close_aria")}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </header>

                <div className="space-y-8 p-8">
                  {loading ? (
                    <div className="flex flex-col items-center justify-center gap-4 py-12">
                      <Loader2 className="h-10 w-10 animate-spin text-green-500" />
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {t("modal_history_loading")}
                      </p>
                    </div>
                  ) : error ? (
                    <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30 px-4 py-6 text-center text-sm text-rose-700 dark:text-rose-300">
                      {error}
                    </div>
                  ) : history.length === 0 ? (
                    <div className="rounded-lg border border-slate-200 dark:border-white/5 bg-slate-50 dark:bg-[#131711] px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                      {t("modal_history_empty")}
                    </div>
                  ) : (
                    <section>
                      <div className="mb-4 flex items-center gap-2">
                        <History className="h-4 w-4 text-slate-400" />
                        <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-[#a4b79e]">
                          {t("modal_history_section_title")}
                        </h3>
                      </div>
                      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                        {history.map((item, index) => (
                          <div
                            key={item.id}
                            className="rounded-lg border border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-[#131711] p-4 space-y-4"
                          >
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                              <div className="flex items-center gap-4 min-w-0">
                                <UserAvatar name={item.user_name} />
                                <div className="flex flex-col min-w-0">
                                  <p className="text-base font-bold text-slate-900 dark:text-white truncate">
                                    {item.user_name}
                                  </p>
                                  <p className="text-sm text-slate-500 dark:text-[#a4b79e]">
                                    {formatDateTime(item.created_at)}
                                  </p>
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#a4b79e] mt-0.5">
                                    {changeTypeLabels[item.change_type]}
                                    {index < history.length - 1 ? (
                                      <span className="ml-1.5 text-slate-300 dark:text-slate-600">
                                        · #{index + 1}
                                      </span>
                                    ) : null}
                                  </span>
                                </div>
                              </div>
                              {item.duration_minutes != null && (
                                <div className="rounded-lg bg-green-500/10 px-3 py-2 shrink-0">
                                  <p className="text-[10px] font-bold uppercase text-green-600/70 dark:text-green-400/70">
                                    {t("modal_history_duration")}
                                  </p>
                                  <p className="text-sm font-bold text-green-600 dark:text-green-400">
                                    {formatDurationMinutes(item.duration_minutes)}
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="pt-3 border-t border-slate-200 dark:border-white/5">
                              {item.previous_status == null ? (
                                <div className="flex flex-col items-start gap-1.5">
                                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-[#a4b79e]">
                                    {t("modal_history_initial_status")}
                                  </p>
                                  <span
                                    className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-bold ${getStatusBadgeClass(
                                      item.new_status
                                    )}`}
                                  >
                                    <CheckCircle className="h-3.5 w-3.5" />
                                    {operationStatusLabels[item.new_status]}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between gap-4 flex-wrap">
                                  <div className="flex-1 min-w-0">
                                    <p className="mb-1.5 text-[10px] font-bold uppercase text-slate-400">
                                      {t("modal_history_previous_status")}
                                    </p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span
                                        className={`rounded-full border px-3 py-1.5 text-xs font-bold shrink-0 ${getStatusBadgeClass(
                                          item.previous_status
                                        )}`}
                                      >
                                        {operationStatusLabels[item.previous_status]}
                                      </span>
                                      <ArrowRight className="h-4 w-4 text-slate-300 dark:text-slate-500 shrink-0" />
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-end shrink-0">
                                    <p className="mb-1.5 text-[10px] font-bold uppercase text-green-600 dark:text-green-400">
                                      {t("modal_history_current_status")}
                                    </p>
                                    <span
                                      className={`flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-xs font-bold ${getStatusBadgeClass(
                                        item.new_status
                                      )}`}
                                    >
                                      <CheckCircle className="h-3.5 w-3.5" />
                                      {operationStatusLabels[item.new_status]}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </div>

                <div className="flex gap-3 border-t border-slate-100 dark:border-white/10 px-8 pb-8 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 py-4 rounded-lg bg-green-500 font-bold text-white transition-all hover:brightness-110 flex items-center justify-center gap-2 dark:bg-green-500 dark:text-white"
                  >
                    {t("modal_history_close")}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
